/**
 * Application constants and configuration.
 * Centralizes all magic numbers, strings, and configuration values.
 */

// Bounty configuration
export const BOUNTY_CONFIG = {
  FREE_MIN_REWARD_CENTS: 200, // $2.00 minimum for free users
  FREE_MAX_REWARD_CENTS: 1000000, // $10,000 maximum for free users
  PRO_MIN_REWARD_CENTS: 50, // $0.50 minimum for Pro users
  PRO_MAX_REWARD_CENTS: 1000000, // $10,000 maximum for Pro users
  HUNTER_PAYOUT_PERCENTAGE: 1.0, // Hunters keep 100% of the reward - the poster pays our fee on top
  PLATFORM_FEE_PERCENTAGE: 0.20, // 20% service fee, charged to the poster on top of the reward
  PRO_PLATFORM_FEE_PERCENTAGE: 0.10, // Pro posters pay half rate - the main reason to subscribe
  MIN_PLATFORM_FEE_CENTS: 40, // $0.40 floor so tiny bounties still cover their own overhead
  PRO_MIN_PLATFORM_FEE_CENTS: 20, // Half rate means half floor, so Pro is cheaper at every size
  EXPIRY_HOURS: 168,
  DEFAULT_DURATION_HOURS: 168,
  FREE_MAX_DURATION_HOURS: 168,
  PRO_MAX_DURATION_HOURS: 720,
  MIN_HUNTERS: 1,
  MAX_HUNTERS: 10,
  MAX_BULK_LISTINGS: 10,
  MAX_PAYABLE_CENTS: 99999999,
  MAX_TITLE_LENGTH: 120,
  MAX_DESCRIPTION_LENGTH: 1000,
  MAX_LOCATION_LENGTH: 160,
  MIN_TITLE_LENGTH: 5,
  MIN_DESCRIPTION_LENGTH: 10,
} as const;

// Marketing gate only. Leave unset until the server migration is enabled.
// Eligibility, grants, reservations, and redemption are enforced by the server.
export const INTRO_OFFER_ENABLED = import.meta.env.VITE_INTRO_OFFER_ENABLED === 'true';

export const INTRO_OFFER_CONFIG = {
  credits: 5,
  maxDiscountCents: 100,
  redemptionDays: 30,
  listingDurationHours: 720,
} as const;

export const INTRO_OFFER_TERMS =
  "Available to accounts created on or after the offer launch, granted once at first verified email confirmation. " +
  "Redeem within 30 days of confirmation; one credit per listing, including each bulk listing. " +
  "Each credited listing runs for 30 days from publication. You still fund rewards, remaining fees, and extras. " +
  "Intro credits cannot stack with backer credits, which retain their separate full-service-fee waiver. " +
  "Unused credit value is discarded; credits have no cash value, cannot be transferred, and are not restored after ordinary cancellation of a published listing. " +
  "Failed or unpaid attempts release reserved credits without extending their original expiry.";

/**
 * Service fee charged to the poster, on top of the reward pool.
 *
 * Hunters always receive the full reward, so this is the platform's only
 * per-bounty revenue. Keep every call site on this helper so the checkout
 * summary, the bulk-post path and the charged amount can never disagree.
 *
 * Pro pays half the rate and half the floor, so it is cheaper at every size.
 */
export const bountyFeeRate = (isPro: boolean): number =>
  isPro ? BOUNTY_CONFIG.PRO_PLATFORM_FEE_PERCENTAGE : BOUNTY_CONFIG.PLATFORM_FEE_PERCENTAGE;

export const bountyFeeFloorCents = (isPro: boolean): number =>
  isPro ? BOUNTY_CONFIG.PRO_MIN_PLATFORM_FEE_CENTS : BOUNTY_CONFIG.MIN_PLATFORM_FEE_CENTS;

export const calculateBountyFeeCents = (rewardPoolCents: number, isPro = false): number =>
  Math.max(bountyFeeFloorCents(isPro), Math.round(rewardPoolCents * bountyFeeRate(isPro)));

// Auth configuration
export const AUTH_CONFIG = {
  MIN_PASSWORD_LENGTH: 6,
  MAX_PASSWORD_LENGTH: 72,
  MAX_EMAIL_LENGTH: 255,
} as const;

// Email validation - blocked disposable domains for spam prevention
export const BLOCKED_EMAIL_DOMAINS = [
  'gptmail.ca',
  'gptmail.com',
  'mailinator.com',
  'guerrillamail.com',
  'guerrillamail.org',
  'guerrillamail.net',
  'temp-mail.org',
  'tempmail.com',
  'tempmail.net',
  '10minutemail.com',
  'throwaway.email',
  'yopmail.com',
  'yopmail.fr',
  'sharklasers.com',
  'trashmail.com',
  'trashmail.net',
  'maildrop.cc',
  'getnada.com',
  'inboxbear.com',
  'dispostable.com',
  'burnermail.io',
  'mailmetrash.com',
  'mailexpire.com',
  'dropmail.me',
  'emailfake.com',
  'fakemailgenerator.com',
  'mailnator.com',
  'suremail.info',
  'spambox.us',
  'mytemp.email',
] as const;

/**
 * Checks if email uses a blocked/spam domain.
 */
export function isAllowedEmail(email: string): boolean {
  const domain = email.split('@')[1]?.toLowerCase();
  if (!domain) return false;
  
  // Check against blocked domains
  if (BLOCKED_EMAIL_DOMAINS.includes(domain as any)) return false;
  
  // Check for suspicious numbered patterns (e.g. shunt_1234567890_d3c7@domain)
  // Spam bots often use uderid_timestamp_hash@domain format
  const localPart = email.split('@')[0]?.toLowerCase();
  if (localPart && /^[a-z]+_\d{6,}_[a-z0-9]{3,6}@/i.test(email)) return false;
  
  return true;
}

// Route paths
export const ROUTES = {
  HOME: "/",
  AUTH: "/auth",
  ONBOARDING: "/onboarding",
  POST_BOUNTY: "/post",
  PROFILE: "/profile",
  SUBSCRIPTION: "/subscription",
  ANALYTICS: "/analytics",
  MESSAGES: "/messages",
  DEPOSIT: "/deposit",
  SETTINGS: "/settings",
  BOUNTY_DETAIL: (id: string) => `/bounty/${id}`,
  EDIT_BOUNTY: (id: string) => `/edit/${id}`,
} as const;

// Storage configuration
export const STORAGE_CONFIG = {
  PROOFS_BUCKET: "proofs",
  WALL_BUCKET: "wall-images",
  SIGNED_URL_EXPIRY_SECONDS: 3600, // 1 hour
} as const;

// UI configuration
export const UI_CONFIG = {
  BOUNTY_FEED_LIMIT: 60,
  SKELETON_COUNT: 6,
} as const;

// Proof type display configuration
export const PROOF_TYPE_CONFIG = {
  photo: { label: "Photo", requiresFile: true },
  screenshot: { label: "Screenshot", requiresFile: true },
  link: { label: "Link", requiresFile: false },
  text: { label: "Text answer", requiresFile: false },
} as const;

// Status display configuration
export const STATUS_CONFIG = {
  open: { label: "Open", canClaim: true },
  claimed: { label: "Claimed", canClaim: false },
  submitted: { label: "Submitted", canClaim: false },
  completed: { label: "Completed", canClaim: false },
  expired: { label: "Expired", canClaim: false },
  disputed: { label: "Disputed", canClaim: false },
} as const;

// Pro subscription configuration
export const PRO_CONFIG = {
  TIERS: {
    MONTHLY: { id: "monthly", label: "Monthly", price_cents: 999, duration_days: 30 },
    YEARLY: { id: "yearly", label: "Yearly", price_cents: 7999, duration_days: 365, savings_percent: 33 },
  },
  FEATURES: {
    LOWER_FEES: `Half-price service fee (${Math.round(
      BOUNTY_CONFIG.PRO_PLATFORM_FEE_PERCENTAGE * 100
    )}% vs ${Math.round(BOUNTY_CONFIG.PLATFORM_FEE_PERCENTAGE * 100)}%)`,
    CUSTOM_REWARDS: "Rewards from $0.50 to $10,000 per hunter",
    EXTENDED_DURATION: "7-day default duration, up to 30 days",
    PRIORITY_LISTING: "Priority listing on bounty board",
    ANALYTICS: "Advanced analytics dashboard",
    BULK_POSTING: "Bulk posting (up to 10 listings per batch)",
    CUSTOM_PROOF: "Custom proof requirements",
    VERIFICATION_BADGE: "Verified hunter badge",
    DIRECT_MESSAGING: "Direct messaging with hunters",
    PRIORITY_SUPPORT: "Priority customer support",
  },
  DURATION_OPTIONS: [
    { label: "24 hours", value: 24 },
    { label: "48 hours", value: 48 },
    { label: "72 hours", value: 72 },
    { label: "7 days", value: 168 },
    { label: "14 days", value: 336 },
    { label: "30 days", value: 720 },
  ],
} as const;

// Error messages
export const ERROR_MESSAGES = {
  AUTH_REQUIRED: "Authentication required",
  BOUNTY_NOT_FOUND: "Bounty not found",
  FAILED_TO_LOAD: "Failed to load data",
  FAILED_TO_SUBMIT: "Failed to submit",
  INVALID_INPUT: "Invalid input",
  NETWORK_ERROR: "Network error occurred",
  UNEXPECTED_ERROR: "An unexpected error occurred",
} as const;

// Success messages
export const SUCCESS_MESSAGES = {
  BOUNTY_POSTED: "Bounty posted successfully!",
  BOUNTY_CLAIMED: "Bounty claimed! Submit proof to earn the reward.",
  PROOF_SUBMITTED: "Proof submitted! Awaiting verification.",
  BOUNTY_APPROVED: "Bounty approved! Payment processed.",
  ACCOUNT_CREATED: "Account created successfully!",
  LOGIN_SUCCESS: "Welcome back, hunter!",
  UPGRADED_TO_PRO: "Welcome to Pro! Enjoy yourSheriff's Badge!",
  SUBSCRIPTION_UPDATED: "Subscription updated successfully!",
} as const;

// Pro-specific messages
export const PRO_MESSAGES = {
  UPGRADE_PROMPT: "Upgrade to Pro to unlock custom rewards and more!",
  CUSTOM_REWARD_LOCKED: "Custom rewards are a Pro feature",
  EXTENDED_DURATION_LOCKED: "Extended duration is a Pro feature",
  ANALYTICS_LOCKED: "Analytics dashboard is a Pro feature",
  PRIORITY_LISTING_ACTIVE: "Your bounty is featured with priority listing!",
} as const;
